<template>
  <div class="dynamic_wrap">
    <div class="dynamic-info container">
      <div class="item-image">
        <img
          :src="getImageFromWaxpeer(pageInfo?.image_url || '')"
          alt=""
        >
      </div>
      <div class="item-info">
        <div class="item-info__header">
          <h1 class="item-name">
            <span>{{ local.user_text }}</span>
          </h1>
        </div>
        <span class="item-description">
          {{ local.user_description }}
        </span>
      </div>
    </div>

    <div
      v-if="isLoading"
      class="load_period loading-container mt-10"
    >
      <div class="f fc">
        <i class="i-loading ml10" />
      </div>
    </div>
    <div
      v-else
      :class="{ container: items.length <= 7, p0: currW < 1000 }"
      class="pb60 f fs ft gap32 wrapper"
    >
      <div
        v-if="items.length"
        class="fg dynamic-items"
      >
        <UiItemsGrid>
          <LazyPagesColorsComponentsColorItemCard
            v-for="item in items"
            :key="item?.market_hash_name"
            :item="item"
            :link="getItemLink({ name: item?.market_hash_name, slug: item?.link })"
          />
        </UiItemsGrid>

        <div class="container">
          <PaginateV2
            key="paginate"
            :pages-length="pagesLength"
            use-query
          />
        </div>
      </div>
      <LazyUiNoResult
        v-if="!isLoading && !items?.length"
        class="mxa"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type {
  AdvancedSearchBody,
  DynamicPageFullInfo,
  DynamicPageLocalType,
} from '~/graphql/generated/schema-types';
import { useResize } from '~/composables/resize';

const props = defineProps<{
  isLoading: boolean
  items: AdvancedSearchBody[]
  pageInfo: DynamicPageFullInfo
  pagesLength: number
  local: DynamicPageLocalType
}>();

const currW = useResize();
const cachedItems = ref<AdvancedSearchBody[]>([]);

watch(props.items, () => {
  if (cachedItems.value.length) {
    return;
  }
  cachedItems.value = props.items.length > 2 ? [props.items[0], props.items[props.items.length - 1]] : props.items.slice(0, 2);
}, { immediate: true });
</script>

<style lang="scss" scoped>
.dynamic {
  &_wrap {
    margin-top: 3rem;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 7rem;

    @media screen and (max-width: 1250px) {
      margin-top: 0;
    }
  }

  &-info {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 4.8rem;
    color: var(--surfaces-on-surface);

    .item-image {
      position: relative;
      width: 280px;
      height: 258px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        width: 280px;
        height: 280px;
        object-position: center;
        object-fit: contain;
      }

      @media screen and (max-width: 525px) {
        width: 100%;
        min-width: 100%;
      }
    }

    .item-info {
      display: flex;
      flex-direction: column;
      gap: 20px;

      &__header {
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .item-name {
        font-weight: 600;
        font-size: 2.8rem;
        line-height: 3.8rem;
      }

      .item-description {
        font-size: 1.6rem;
        opacity: 0.8;
        max-width: 68rem;
      }
    }
  }
}

@media screen and (max-width: 1075px) {
  .dynamic {
    .dynamic_wrap {
      gap: 4rem;
    }

    &-info {
      .item-name {
        font-size: 2.4rem;
        line-height: 3.4rem;
      }

      .item-description {
        font-size: 1.4rem;
        line-height: 3rem;
      }
    }
  }
}

@media screen and (max-width: 979px) {
  .dynamic-items {
    padding-right: 16px;
    padding-left: 16px;
  }
}

@media screen and (max-width: 850px) {
  .dynamic-info {
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .item-info {
      align-items: center;
      width: 100%;
    }
  }
}

@media screen and (max-width: 768px) {
  .dynamic {
    .dynamic_wrap {
      gap: 4rem;
    }

    .fg {
      width: 100%;
    }

    .wrapper {
      flex-direction: column;
    }

    &-info {
      gap: 0;

      .item-info {
        margin-left: 0;
        margin-top: 1.6rem;

        .item-name {
          font-size: 2.2rem;
          line-height: 3.2rem;
        }

        .item-description {
          margin-top: 1.2rem;
          line-height: 2.8rem;
        }
      }
    }
  }
}

@media screen and (max-width: 525px) {
  .dynamic {
    .dynamic_wrap {
      gap: 1.6rem;
    }

    &-info {
      .item-image {
        margin-left: 1.5rem;
        margin-right: 1.5rem;
        background: rgba(33, 40, 53, 0.2);
        border-radius: 1.2rem;
      }

      .item-info {
        .item-name {
          font-size: 2rem;
          line-height: 2.7rem;
        }

        .item-description {
          margin-top: 0.8rem;
          line-height: 2.6rem;
        }
      }
    }
  }
}
</style>
